import { createApi } from '@reduxjs/toolkit/query/react';
import { createAuthBaseQuery } from './baseQuery';
import { API_BASE_URL } from '../constants/api';

// Corporate Portal API — per-client settings, workspace data, and
// the statutory/compliance surfaces. Every endpoint here hits an
// authenticated route under /api/portal/corp/* and is scoped
// server-side by the logged-in user's `client_id` (so the wire
// never carries the client_id — JWT alone identifies the tenant).

export const corporateApi = createApi({
  reducerPath: 'corporateApi',
  baseQuery: createAuthBaseQuery(API_BASE_URL),
  tagTypes: ['CorpSettings', 'Statutory', 'Capa', 'Inspections', 'Rounds', 'Certificates', 'WorkforceAggregate'],
  endpoints: (builder) => ({
    // ─── Settings ────────────────────────────────────────────
    getCorpSettings: builder.query({
      query: () => '/portal/corp/settings',
      transformResponse: (res) => res?.settings || null,
      providesTags: ['CorpSettings'],
    }),
    updateCorpSettings: builder.mutation({
      query: (patch) => ({
        url: '/portal/corp/settings',
        method: 'PUT',
        body: patch,
      }),
      transformResponse: (res) => res?.settings || null,
      invalidatesTags: ['CorpSettings'],
    }),

    // ─── Statutory Register ──────────────────────────────────
    // Scoped by FY — the calling page passes the active FY arg, RTK
    // caches each FY separately so flipping between years is instant
    // once both are warm.
    listStatutory: builder.query({
      query: (fy) => `/portal/corp/statutory?fy=${encodeURIComponent(fy)}`,
      transformResponse: (res) => res?.items || [],
      providesTags: (result, _err, fy) => [{ type: 'Statutory', id: fy }],
    }),
    createStatutory: builder.mutation({
      query: (body) => ({ url: '/portal/corp/statutory', method: 'POST', body }),
      transformResponse: (res) => res?.item,
      invalidatesTags: (_r, _e, body) => [{ type: 'Statutory', id: body?.fy }],
    }),
    updateStatutory: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/portal/corp/statutory/${id}`, method: 'PATCH', body }),
      transformResponse: (res) => res?.item,
      invalidatesTags: (_r, _e, arg) => [{ type: 'Statutory', id: arg?.fy }],
    }),
    deleteStatutory: builder.mutation({
      query: ({ id }) => ({ url: `/portal/corp/statutory/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'Statutory', id: arg?.fy }],
    }),

    // ─── CAPA ────────────────────────────────────────────────
    listCapa: builder.query({
      query: (fy) => `/portal/corp/capa?fy=${encodeURIComponent(fy)}`,
      transformResponse: (res) => res?.items || [],
      providesTags: (result, _err, fy) => [{ type: 'Capa', id: fy }],
    }),
    createCapa: builder.mutation({
      query: (body) => ({ url: '/portal/corp/capa', method: 'POST', body }),
      transformResponse: (res) => res?.item,
      invalidatesTags: (_r, _e, body) => [{ type: 'Capa', id: body?.fy }],
    }),
    updateCapa: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/portal/corp/capa/${id}`, method: 'PATCH', body }),
      transformResponse: (res) => res?.item,
      invalidatesTags: (_r, _e, arg) => [{ type: 'Capa', id: arg?.fy }],
    }),
    deleteCapa: builder.mutation({
      query: ({ id }) => ({ url: `/portal/corp/capa/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'Capa', id: arg?.fy }],
    }),

    // ─── Inspection Records ──────────────────────────────────
    listInspections: builder.query({
      query: (limit = 20) => `/portal/corp/inspections?limit=${limit}`,
      transformResponse: (res) => res?.items || [],
      providesTags: ['Inspections'],
    }),
    createInspection: builder.mutation({
      query: (body) => ({ url: '/portal/corp/inspections', method: 'POST', body }),
      transformResponse: (res) => res?.item,
      invalidatesTags: ['Inspections'],
    }),
    updateInspection: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/portal/corp/inspections/${id}`, method: 'PATCH', body }),
      transformResponse: (res) => res?.item,
      invalidatesTags: ['Inspections'],
    }),
    deleteInspection: builder.mutation({
      query: (id) => ({ url: `/portal/corp/inspections/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Inspections'],
    }),

    // ─── Checkup Rounds ──────────────────────────────────────
    listRounds: builder.query({
      query: (fy) => `/portal/corp/rounds?fy=${encodeURIComponent(fy)}`,
      transformResponse: (res) => res?.items || [],
      providesTags: (result, _err, fy) => [{ type: 'Rounds', id: fy }],
    }),
    createRound: builder.mutation({
      query: (body) => ({ url: '/portal/corp/rounds', method: 'POST', body }),
      transformResponse: (res) => res?.item,
      invalidatesTags: (_r, _e, body) => [{ type: 'Rounds', id: body?.fy }],
    }),
    updateRound: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/portal/corp/rounds/${id}`, method: 'PATCH', body }),
      transformResponse: (res) => res?.item,
      invalidatesTags: (_r, _e, arg) => [{ type: 'Rounds', id: arg?.fy }],
    }),
    deleteRound: builder.mutation({
      query: ({ id }) => ({ url: `/portal/corp/rounds/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'Rounds', id: arg?.fy }],
    }),

    // ─── Certificate Expiries ────────────────────────────────
    // FY-independent. One global tag — every mutation invalidates
    // the list since the alerts feed is small (<100 rows).
    listCertificates: builder.query({
      query: () => '/portal/corp/certificates',
      transformResponse: (res) => res?.items || [],
      providesTags: ['Certificates'],
    }),
    createCertificate: builder.mutation({
      query: (body) => ({ url: '/portal/corp/certificates', method: 'POST', body }),
      transformResponse: (res) => res?.item,
      invalidatesTags: ['Certificates'],
    }),
    updateCertificate: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/portal/corp/certificates/${id}`, method: 'PATCH', body }),
      transformResponse: (res) => res?.item,
      invalidatesTags: ['Certificates'],
    }),
    deleteCertificate: builder.mutation({
      query: (id) => ({ url: `/portal/corp/certificates/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Certificates'],
    }),

    // ─── Workforce Aggregate (IT Dose-derived) ───────────────
    // Single read-only query that powers Overview KPIs, Analytics
    // and ESG. Cached by FY (RTK key includes the arg) so flipping
    // the year doesn't re-aggregate the previous FY.
    getWorkforceAggregate: builder.query({
      query: (fy) => `/portal/corp/workforce-aggregate?fy=${encodeURIComponent(fy)}`,
      providesTags: (result, _err, fy) => [{ type: 'WorkforceAggregate', id: fy }],
    }),
  }),
});

export const {
  useGetCorpSettingsQuery,
  useUpdateCorpSettingsMutation,
  useListStatutoryQuery,
  useCreateStatutoryMutation,
  useUpdateStatutoryMutation,
  useDeleteStatutoryMutation,
  useListCapaQuery,
  useCreateCapaMutation,
  useUpdateCapaMutation,
  useDeleteCapaMutation,
  useListInspectionsQuery,
  useCreateInspectionMutation,
  useUpdateInspectionMutation,
  useDeleteInspectionMutation,
  useListRoundsQuery,
  useCreateRoundMutation,
  useUpdateRoundMutation,
  useDeleteRoundMutation,
  useListCertificatesQuery,
  useCreateCertificateMutation,
  useUpdateCertificateMutation,
  useDeleteCertificateMutation,
  useGetWorkforceAggregateQuery,
} = corporateApi;
